import { useQueryClient } from '@tanstack/react-query';
import {
  DocumentGroupRenderHelpers,
  DocumentTypeGroup,
  DocumentTypesAccess,
} from '../../../components/document-type-access';
import { WorkflowScopes } from '../../../constants/workflows';
import { useDocumentSelection } from '../../../hooks/useDocumentSelection';
import { DocumentRecordCard } from './DocumentRecordCard';
import { AddBar, RecordSection } from './ProviderDrawerPrimitives';
import { STAFF_PROVIDER_DOCUMENTS_COUNT_QUERY_KEY } from './queryKeys';

export type DocumentSectionSpec = {
  /** DocumentType slug. */
  slug: string;
  /**
   * Section title for `count` documents (singular for exactly one, plural otherwise).
   * Defaults to the DocumentType's own title.
   */
  title?: (count: number) => string;
  /** Test id of the section; defaults to `<testIdPrefix>-<slug>`. */
  testId?: string;
  /**
   * A clinician is expected to have at least one of these. While none is on file the
   * type is flagged in the upload bar, and the tab's label carries an attention icon
   * (`useMissingRequiredDocuments`).
   */
  required?: boolean;
};

type DocumentSectionsProps = {
  providerId: string;
  /**
   * The types this tab owns. This list IS the display order — of the upload bar's
   * buttons and of the sections — whatever order the API returns types or documents in.
   */
  types: readonly DocumentSectionSpec[];
  /** Prefix of the upload bar (`-upload-bar`), empty line (`-empty`) and section test ids. */
  testIdPrefix: string;
  /**
   * Who and what these documents are, for the name of a zip of them:
   * "John Joe MD Education Documents" → "John Joe MD Education Documents 20261001-1532.zip"
   * (first name first: `providerFileName`).
   */
  zipSubject: string;
  /** Upload bar lead-in when none of the types has a document. */
  noneOnFileLead?: string;
  /** Upload bar lead-in when some types have documents and these do not. */
  someOnFileLead?: string;
  /** Shown instead of the bar when nothing is on file and the viewer may not upload. */
  emptyText?: string;
  /** Extra queries the host must refetch after a change (the documents count is handled here). */
  onDocumentsChanged?: () => void;
};

/**
 * The standard way a staff clinician drawer tab shows documents of one or more types
 * (patterns/add-in-context-document-access.md):
 * - a type WITH documents is a section of cards, "+ Add one" at the right of its title bar
 * - the types with NOTHING on file never render a section: they are shortcut buttons in
 *   one upload bar, each opening the upload dialog pinned to that type
 * - nobody is sent to the Documents tab to pick from the dropdown of every type
 * - documents the viewer may download carry a checkbox (card: upper left; section title
 *   bar: all of its cards); a selection brings up a floating bar with Select all /
 *   Invert / Deselect and "Download"
 */
export function DocumentSections({
  providerId,
  types,
  onDocumentsChanged,
  ...rest
}: DocumentSectionsProps) {
  const queryClient = useQueryClient();
  return (
    <DocumentTypesAccess
      contextType={WorkflowScopes.Provider}
      contextId={providerId}
      ownerId={providerId}
      documentTypeSlugs={types.map((type) => type.slug)}
      onDocumentsChanged={() => {
        queryClient.invalidateQueries({
          queryKey: [STAFF_PROVIDER_DOCUMENTS_COUNT_QUERY_KEY, providerId],
        });
        onDocumentsChanged?.();
      }}
    >
      {(groups, helpers) => (
        <DocumentSectionsBody
          providerId={providerId}
          types={types}
          groups={groups}
          helpers={helpers}
          {...rest}
        />
      )}
    </DocumentTypesAccess>
  );
}

function DocumentSectionsBody({
  providerId,
  types,
  groups,
  helpers,
  testIdPrefix,
  zipSubject,
  noneOnFileLead = 'Nothing on file yet — add:',
  someOnFileLead = 'Not on file yet — add:',
  emptyText = 'No documents on file.',
}: Omit<DocumentSectionsProps, 'onDocumentsChanged'> & {
  groups: DocumentTypeGroup[];
  helpers: DocumentGroupRenderHelpers;
}) {
  const { renderLink, renderMenu, renderUpload, canDownload } = helpers;
  const onFile = groups.filter((group) => group.documents.length > 0);
  const isRequired = (slug: string) => Boolean(types.find((type) => type.slug === slug)?.required);
  // nothing on file: a button when the viewer may upload it; a required type the viewer
  // may NOT upload still has to be seen as missing
  const missing = groups.filter(
    (group) => group.documents.length === 0 && (group.canUpload || isRequired(group.slug))
  );

  // Selection covers this tab's documents only, and only the ones the viewer may
  // download; it resets when the drawer moves to another clinician.
  const selectableIdsOf = (group: DocumentTypeGroup) =>
    group.documents.filter(canDownload).map((document) => document.id);
  const selection = useDocumentSelection({
    resetKey: `${providerId}|${testIdPrefix}`,
    selectableIds: onFile.flatMap(selectableIdsOf),
    zipSubject,
    testIdPrefix,
  });

  return (
    <>
      {missing.length > 0 && (
        <AddBar
          lead={onFile.length > 0 ? someOnFileLead : noneOnFileLead}
          data-testid={`${testIdPrefix}-upload-bar`}
        >
          {missing.map((group) => {
            const required = isRequired(group.slug);
            // a required type leads with the same hazard icon the tab label shows
            const label = (
              <>
                {required ? (
                  <i
                    className="fas fa-fw fa-triangle-exclamation tw:text-amber-700"
                    aria-label="Required"
                  />
                ) : (
                  <i className="fas fa-fw fa-plus" aria-hidden="true" />
                )}{' '}
                {group.title}
              </>
            );
            return (
              <span
                key={group.slug}
                data-required={required ? true : undefined}
                data-testid={`${testIdPrefix}-missing-${group.slug}`}
              >
                {group.canUpload ? (
                  renderUpload(group.slug, { text: label })
                ) : (
                  <span className="tw:text-sm tw:text-amber-700">{label}</span>
                )}
              </span>
            );
          })}
          {missing.some((group) => isRequired(group.slug)) && (
            <span className="tw:text-xs tw:text-amber-700">
              <i className="fas fa-triangle-exclamation tw:mr-1" aria-hidden="true" />
              required
            </span>
          )}
        </AddBar>
      )}
      {onFile.length === 0 && missing.length === 0 && (
        <div className="tw:text-sm tw:text-zinc-500" data-testid={`${testIdPrefix}-empty`}>
          {emptyText}
        </div>
      )}
      {onFile.map((group) => {
        const spec = types.find((type) => type.slug === group.slug);
        const title = spec?.title ? spec.title(group.documents.length) : group.title;
        return (
          <RecordSection
            key={group.slug}
            title={title}
            count={group.documents.length}
            emptyText={null}
            leading={selection.renderGroupCheckbox(
              selectableIdsOf(group),
              title,
              `${testIdPrefix}-select-all-${group.slug}`
            )}
            action={renderUpload(group.slug, {
              as: 'link',
              text: (
                <>
                  <i className="fas fa-plus tw:mr-1" aria-hidden="true" />
                  Add one
                </>
              ),
            })}
            data-testid={spec?.testId ?? `${testIdPrefix}-${group.slug}`}
          >
            {group.documents.map((document) => (
              <DocumentRecordCard
                key={document.id}
                document={document}
                renderLink={renderLink}
                renderMenu={renderMenu}
                testIdPrefix={`${testIdPrefix}-document`}
                leading={
                  canDownload(document)
                    ? selection.renderCheckbox(
                        document.id,
                        document.label || document.original_filename
                      )
                    : null
                }
              />
            ))}
          </RecordSection>
        );
      })}
      {selection.bar}
    </>
  );
}
